/** Transforms BFF config file of configurablePage app to the JSON structure expected by configurable-page-service */

import {
  TConfig,
  TConfigBackend,
  TPreviewBlockBackend,
  TSearchRequest,
  TSearchRequestBackend,
} from '../../client/js/types/TConfigurablePage';
import isEmpty from 'lodash/isEmpty';

const transformHeader = (header) => {
  if (!header || !(header.imageUrl || header.body)) {
    return;
  }

  return {
    body: header.body,
    media: {
      type: 'image',
      pictures: [{ large: header.imageUrl }],
    },
  };
};

const transformAttributeIds = (attributeIds) => attributeIds.map((attrId) => ({ attributeValueId: attrId }));

const transformAttributeRanges = (attributeRanges) =>
  attributeRanges.map((attributeRange) => {
    const [attributeKey, from, to] = attributeRange.split(':');
    return { attributeKey, from, to };
  });

const transformAttributeKeys = (attributeKeys) =>
  attributeKeys.map((attributeKeyValue) => {
    const [attributeKey, attributeValueKey] = attributeKeyValue.split(':');
    return { attributeKey, attributeValueKey };
  });

export const transformSearchRequest = (searchRequest): TSearchRequestBackend | void => {
  if (!searchRequest) {
    return;
  }

  const {
    l1CategoryId,
    l2CategoryId,
    attributeIds,
    attributeRanges,
    attributeKeys,
    sorting,
    query,
    offset,
    limit,
    sellerIds,
  } = searchRequest;

  const id = l2CategoryId || l1CategoryId;

  const parentId = l2CategoryId ? l1CategoryId : undefined;

  return {
    filters: {
      ...(id ? { categories: [{ id, parentId }] } : {}),
      ...(query ? { query: { text: query } } : {}),
      ...(attributeIds?.length ? { attributesById: transformAttributeIds(attributeIds) } : {}),
      ...(attributeKeys?.length ? { attributesByKey: transformAttributeKeys(attributeKeys) } : {}),
      ...(attributeRanges?.length ? { attributeRanges: transformAttributeRanges(attributeRanges) } : {}),
      ...(sellerIds?.length ? { sellerIds } : {}),
    },
    ...(sorting?.sortBy ? { sorting: { sortOrder: sorting.sortOrder, sortBy: sorting.sortBy } } : {}),
    limit: limit,
    offset: offset || 0,
  };
};

const transformPreviewBlocks = (previewBlocks): TPreviewBlockBackend[] | void => {
  if (!previewBlocks) {
    return;
  }

  return previewBlocks.map((previewBlock) => ({
    body: previewBlock.body,
    action: previewBlock.action,
    searchRequest: transformSearchRequest(previewBlock.searchRequest) as TSearchRequest,
  }));
};

const transformSections = (sections) =>
  sections.map((section) => ({
    type: section.type,
    body: section.body,
    action: section.action,
    ...(!section.seoContent && {
      content: {
        excludeAdmarkt: section.content.excludeAdmarkt,
        visualization: section.content.visualization,
        ...(!isEmpty(section.content.feedRequest) && { feedRequest: section.content.feedRequest }),
        ...(!isEmpty(section.content.searchRequest) && {
          searchRequest: transformSearchRequest(section.content.searchRequest),
        }),
        ...(!isEmpty(section.content.previewBlocks?.[0]) && {
          previewBlocks: transformPreviewBlocks(section.content.previewBlocks),
        }),
      },
    }),
    seoContent: section.seoContent,
  }));

const clientConfigToBackendConfig = (config: TConfig): TConfigBackend => ({
  header: transformHeader(config.header),
  metaData: config.metaData,
  action: config.action,
  sections: transformSections(config.sections),
});

export default clientConfigToBackendConfig;
